/**
 * useWaitingListOpportunities.js
 *
 * Hook que carrega a lista de espera e processa as oportunidades
 * para o período visível na agenda (diária ou semanal).
 *
 * Retorna um Map<slotKey, opportunityData> onde:
 *   slotKey = "YYYY-MM-DD_HH:MM" (qualquer profissional: visões sem coluna por profissional)
 *          ou "YYYY-MM-DD_HH:MM_r<id>" (só quem pode ser atendido por aquele profissional:
 *          colunas do modo "Profissionais", só entradas que pedem aquele profissional)
 *          ou "YYYY-MM-DD_HH:MM_rany" (entradas "Qualquer profissional" no modo
 *          "Profissionais": um selo só por horário, no eixo de horas);
 *          use getSlotOpportunity() / anyProfessionalSlotOpportunity() para ler
 *   opportunityData = { patientCount, avgScore, relevanceLabel, bestPatient, patients[], resourceId }
 *
 * Profissional livre no horário: cada horário de availableDates traz `resourceIds`
 * (profissionais livres para aquela entrada). Resposta antiga sem o campo: vale o
 * profissional pedido na entrada; sem pedido, qualquer agenda ativa.
 *
 * Cache de 5 minutos para não sobrecarregar o backend.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { buildOpportunitiesFromPatients } from '../../../waiting-list-v2/utils/opportunityMapper';
import { isSlotAvailable } from '../utils/appointmentUtils';

const CACHE_TTL = 5 * 60 * 1000; // 5 minutos

/**
 * Retorna o label de relevância baseado no score médio do slot.
 * @param {number} avgScore
 * @returns {string}
 */
function getRelevanceLabel(avgScore) {
  if (avgScore >= 75) return 'Alta oportunidade';
  if (avgScore >= 55) return 'Boa oportunidade';
  return 'Lista de espera';
}

const MIN_AVG_SCORE = 35;

/** Chave das entradas "Qualquer profissional" no modo "Profissionais" (uma por horário). */
export const ANY_PROFESSIONAL = 'any';

/** Chave do mapa: sem profissional = qualquer agenda; com = só a daquele profissional. */
export function opportunityKey(date, time, resourceId = null) {
  return resourceId == null ? `${date}_${time}` : `${date}_${time}_r${resourceId}`;
}

/**
 * Oportunidade de um horário. `resourceId` undefined = visão sem coluna por
 * profissional (qualquer agenda); null = coluna sem profissional (nada);
 * id = coluna daquele profissional.
 */
export function getSlotOpportunity(map, date, time, resourceId) {
  if (!map) return null;
  if (resourceId === undefined) return map.get(opportunityKey(date, time)) || null;
  if (resourceId == null || resourceId === '') return null;
  return map.get(opportunityKey(date, time, resourceId)) || null;
}

/**
 * Oportunidade que uma coluna da agenda mostra no horário. Coluna de profissional
 * (modo "Profissionais"): só quem pode ser atendido por ele e só com a coluna
 * livre no horário. Coluna de dia: qualquer profissional, como sempre.
 */
export function columnSlotOpportunity(map, { isResourceColumn, resourceId, appointments, date, dateStr, hour, minute, slotIntervalMinutes }) {
  if (!map || map.size === 0) return null;
  const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  if (!isResourceColumn) return getSlotOpportunity(map, dateStr, time);
  const opportunity = getSlotOpportunity(map, dateStr, time, resourceId);
  if (!opportunity) return null;
  return isSlotAvailable(appointments, date, hour, minute, slotIntervalMinutes) ? opportunity : null;
}

/**
 * Entradas "Qualquer profissional" de um horário no modo "Profissionais": um selo
 * só para o horário (no eixo de horas), não um em cada coluna. Cada entrada leva
 * `freeResourceIds`: as agendas visíveis (columnIds) livres para ela no horário,
 * para a pessoa escolher onde marcar. Sem nenhuma agenda livre, nada.
 */
export function anyProfessionalSlotOpportunity(map, { columnIds, appointments, date, dateStr, hour, minute, slotIntervalMinutes }) {
  if (!map || map.size === 0) return null;
  const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  const entry = map.get(opportunityKey(dateStr, time, ANY_PROFESSIONAL));
  if (!entry) return null;
  const visible = new Set((columnIds || []).filter(id => id != null).map(String));
  const freeByColumn = new Map();
  const isColumnFree = (id) => {
    if (!freeByColumn.has(id)) {
      freeByColumn.set(id, isSlotAvailable(appointments, date, hour, minute, slotIntervalMinutes, { resourceId: id }));
    }
    return freeByColumn.get(id);
  };
  const patients = entry.patients
    .map(p => ({ ...p, freeResourceIds: (p.freeResourceIds || []).filter(id => visible.has(id) && isColumnFree(id)) }))
    .filter(p => p.freeResourceIds.length > 0);
  if (patients.length === 0) return null;
  // resourceId null: a agenda sai da escolha de cada entrada, nunca da chave "any"
  return { ...entry, resourceId: null, patients, patientCount: patients.length, bestPatient: patients[0], anyProfessional: true };
}

/** Profissionais livres para a entrada naquele horário (null = sem como saber). */
function freeResourceIdsFor(item, datetime) {
  const minute = String(datetime || '').slice(0, 16);
  const ad = (item?.availableDates || []).find(a => String(a.datetime || '').slice(0, 16) === minute);
  if (Array.isArray(ad?.resourceIds)) return ad.resourceIds.map(String);
  if (item?.resourceId) return [String(item.resourceId)];
  return null;
}

function buildEntry(slot, patients, resourceId = null) {
  const avgScore = patients.length > 0
    ? Math.round(patients.reduce((sum, p) => sum + p.score, 0) / patients.length)
    : 0;
  return {
    slotKey: opportunityKey(slot.date, slot.time, resourceId),
    date: slot.date,
    time: slot.time,
    resourceId,
    patientCount: patients.length,
    avgScore,
    relevanceLabel: getRelevanceLabel(avgScore),
    bestPatient: patients[0] || null,
    patients,
  };
}

/**
 * Hook principal.
 *
 * @param {Object} params
 * @param {string} params.startDate - "YYYY-MM-DD"
 * @param {string} params.endDate   - "YYYY-MM-DD"
 * @param {string} params.viewMode  - 'daily' | 'weekly' | 'monthly'
 * @returns {{ opportunitiesMap: Map, loading: boolean, reload: Function }}
 */
export function useWaitingListOpportunities({ startDate, endDate, viewMode }) {
  const {
    GetWaitingList,
    GetPatientById,
    GetProcedureById,
    GetResourceById,
    CompanyConfig,
  } = DataProviderInstance();

  const [opportunitiesMap, setOpportunitiesMap] = useState(new Map());
  const [loading, setLoading] = useState(false);

  // Cache interno: { data: waitingListItems[], timestamp: number, key: string }
  const cacheRef = useRef(null);

  const dailyGoal = CompanyConfig?.DailyGoal || 8;

  const buildMap = useCallback((waitingListItems) => {
    if (!waitingListItems || !waitingListItems.length) {
      setOpportunitiesMap(new Map());
      return;
    }

    const slots = buildOpportunitiesFromPatients(waitingListItems, {
      getPatientById: (id) => GetPatientById(id),
      getProcedureById: (id) => GetProcedureById(id),
      getResourceById: (id) => GetResourceById(id),
      dailyGoal,
    });

    // Agendas ativas: entrada "qualquer profissional" sem resourceIds (resposta antiga)
    const activeResourceIds = (CompanyConfig?.Resources || [])
      .filter(r => r.Active == null || r.Active == 1) // eslint-disable-line eqeqeq
      .map(r => String(r.Id));

    // Filtra apenas slots dentro do período visível
    const map = new Map();
    slots.forEach((slot) => {
      if (!slot.date || !slot.time) return;
      if (slot.date < startDate || slot.date > endDate) return;
      if (slot.patientCount < 1) return;

      // Qualquer profissional (visões sem coluna por profissional)
      if (slot.avgScore >= MIN_AVG_SCORE) {
        map.set(opportunityKey(slot.date, slot.time), buildEntry(slot, slot.patients));
      }

      // Modo "Profissionais": quem pede um profissional vai para a coluna dele;
      // "Qualquer profissional" vira uma entrada só do horário (selo no eixo de
      // horas), com as agendas livres para escolher. Os pacientes seguem
      // ordenados por score, como na lista geral
      const byResource = new Map();
      const anyPatients = [];
      slot.patients.forEach((patient) => {
        const ids = freeResourceIdsFor(patient.waitingListItem, slot.datetime) || activeResourceIds;
        if (!patient.waitingListItem?.resourceId) {
          anyPatients.push({ ...patient, freeResourceIds: ids });
          return;
        }
        ids.forEach((id) => {
          if (!byResource.has(id)) byResource.set(id, []);
          byResource.get(id).push(patient);
        });
      });
      byResource.forEach((patients, id) => {
        const entry = buildEntry(slot, patients, id);
        if (entry.avgScore >= MIN_AVG_SCORE) map.set(entry.slotKey, entry);
      });
      if (anyPatients.length > 0) {
        const entry = buildEntry(slot, anyPatients, ANY_PROFESSIONAL);
        if (entry.avgScore >= MIN_AVG_SCORE) map.set(entry.slotKey, entry);
      }
    });

    setOpportunitiesMap(map);
  }, [startDate, endDate, dailyGoal, GetPatientById, GetProcedureById, GetResourceById, CompanyConfig]);

  const load = useCallback(() => {
    // Não carrega na view mensal
    if (viewMode === 'monthly') {
      setOpportunitiesMap(new Map());
      return;
    }

    if (!startDate || !endDate) return;

    const cacheKey = `${startDate}_${endDate}`;

    // Usa cache se ainda válido e para o mesmo período
    if (
      cacheRef.current &&
      cacheRef.current.key === cacheKey &&
      Date.now() - cacheRef.current.timestamp < CACHE_TTL
    ) {
      buildMap(cacheRef.current.data);
      return;
    }

    setLoading(true);

    GetWaitingList(
      {
        includeAvailability: true,
        daysLimit: 30,
        limit: 100,
        status: 'PENDING,CONTACTED,CONFIRMED',
      },
      (response) => {
        setLoading(false);
        if (response && !response.error) {
          const items = response.waitingList || [];
          cacheRef.current = {
            key: cacheKey,
            data: items,
            timestamp: Date.now(),
          };
          buildMap(items);
        }
      }
    );
  }, [startDate, endDate, viewMode, GetWaitingList, buildMap]);

  // Recarrega quando o período muda
  useEffect(() => {
    load();
  }, [load]);

  // Invalida cache e recarrega (chamado após criar agendamento)
  const reload = useCallback(() => {
    cacheRef.current = null;
    load();
  }, [load]);

  return { opportunitiesMap, loading, reload };
}
